import { useRef } from 'react';
import PropTypes from 'prop-types';
import './styles.css';

const PeriodSpinner = () => <span className="period-content-transition__spinner" aria-hidden="true" />;

const PeriodContentTransition = ({
  loading,
  period,
  resolvedPeriod,
  direction = 'forward',
  label = 'Atualizando período...',
  initialLabel = 'Carregando suas finanças...',
  children,
}) => {
  const cachedContentRef = useRef(null);
  const resolvedPeriodRef = useRef(null);
  const hasResolvedContentRef = useRef(false);
  const pendingPeriodRef = useRef(null);
  const pendingObservedLoadingRef = useRef(false);
  const previousResolvedPeriod = resolvedPeriodRef.current;
  const hasExplicitResolution = resolvedPeriod !== undefined;

  let displayedContent = children;
  let displayedPeriod = period;
  let entering = false;
  const periodChanged = hasResolvedContentRef.current && previousResolvedPeriod !== period;

  if (periodChanged && pendingPeriodRef.current !== period) {
    pendingPeriodRef.current = period;
    pendingObservedLoadingRef.current = false;
  }
  if (periodChanged && loading) pendingObservedLoadingRef.current = true;

  const waitingForPeriod = !hasExplicitResolution && periodChanged
    && (!pendingObservedLoadingRef.current || loading);
  const effectiveLoading = loading || waitingForPeriod || (hasExplicitResolution && resolvedPeriod !== period);

  if (effectiveLoading && hasResolvedContentRef.current) {
    displayedContent = cachedContentRef.current;
    displayedPeriod = previousResolvedPeriod;
  } else if (!effectiveLoading) {
    entering = periodChanged;
    cachedContentRef.current = children;
    resolvedPeriodRef.current = period;
    hasResolvedContentRef.current = true;
    pendingPeriodRef.current = null;
    pendingObservedLoadingRef.current = false;
  }

  if (effectiveLoading && !hasResolvedContentRef.current) {
    return (
      <div className="period-content-transition period-content-transition--initial" aria-busy="true" data-testid="period-content-transition">
        <div className="period-content-transition__initial" role="status" aria-live="polite">
          <PeriodSpinner />
          <span>{initialLabel}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`period-content-transition${effectiveLoading ? ' is-updating' : ''}${entering ? ` is-entering is-${direction}` : ''}`}
      aria-busy={effectiveLoading}
      data-testid="period-content-transition"
      data-transition-direction={entering ? direction : undefined}
    >
      <div className="period-content-transition__content" key={displayedPeriod}>
        {displayedContent}
      </div>

      {effectiveLoading && (
        <div className="period-content-transition__overlay" role="status" aria-live="polite" style={{ pointerEvents: 'none' }}>
          <div className="period-content-transition__status">
            <PeriodSpinner />
            <span>{label}</span>
          </div>
        </div>
      )}
    </div>
  );
};

PeriodContentTransition.propTypes = {
  loading: PropTypes.bool.isRequired,
  period: PropTypes.string.isRequired,
  resolvedPeriod: PropTypes.string,
  direction: PropTypes.oneOf(['forward', 'backward']),
  label: PropTypes.string,
  initialLabel: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default PeriodContentTransition;
